/**
 * A floating (dynamic) virtual joystick. Instead of a fixed position, the base
 * spawns wherever the player first touches down inside its zone — more
 * comfortable across grips/seating positions — and disappears on release.
 *
 * Each zone tracks a single pointer and uses pointer capture, so a left-side
 * and a right-side stick work simultaneously (twin-stick) and a finger that
 * drifts across the screen midway stays bound to the stick it started on.
 *
 * Reports a normalised vector in [-1, 1]: x right-positive, y down-positive.
 */
export interface Joystick {
  readonly value: { x: number; y: number };
  dispose(): void;
}

export interface JoystickOptions {
  /** Extra class on the base element, e.g. 'look' for the right-stick accent. */
  variant?: string;
}

const RADIUS = 56; // px of knob travel before the value saturates at 1

export function createZoneJoystick(zone: HTMLElement, options: JoystickOptions = {}): Joystick {
  const base = document.createElement('div');
  const knob = document.createElement('div');
  base.className = `oj-base floating${options.variant ? ` ${options.variant}` : ''}`;
  knob.className = 'oj-knob';
  base.appendChild(knob);
  base.style.display = 'none';
  document.body.appendChild(base);

  const value = { x: 0, y: 0 };
  let pointerId: number | null = null;
  let originX = 0;
  let originY = 0;

  function show(x: number, y: number) {
    originX = x;
    originY = y;
    base.style.left = `${x}px`;
    base.style.top = `${y}px`;
    base.style.display = 'block';
    knob.style.transform = 'translate(0px, 0px)';
  }

  function move(x: number, y: number) {
    let dx = x - originX;
    let dy = y - originY;
    const len = Math.hypot(dx, dy);
    if (len > RADIUS) {
      dx = (dx / len) * RADIUS;
      dy = (dy / len) * RADIUS;
    }
    knob.style.transform = `translate(${dx}px, ${dy}px)`;
    value.x = dx / RADIUS;
    value.y = dy / RADIUS;
  }

  function reset() {
    pointerId = null;
    value.x = 0;
    value.y = 0;
    base.style.display = 'none';
  }

  const onDown = (e: PointerEvent) => {
    if (pointerId !== null) return;
    pointerId = e.pointerId;
    zone.setPointerCapture(e.pointerId);
    show(e.clientX, e.clientY);
    e.preventDefault();
  };
  const onMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    move(e.clientX, e.clientY);
    e.preventDefault();
  };
  const onUp = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    reset();
  };

  zone.addEventListener('pointerdown', onDown);
  zone.addEventListener('pointermove', onMove);
  zone.addEventListener('pointerup', onUp);
  zone.addEventListener('pointercancel', onUp);

  return {
    value,
    dispose() {
      zone.removeEventListener('pointerdown', onDown);
      zone.removeEventListener('pointermove', onMove);
      zone.removeEventListener('pointerup', onUp);
      zone.removeEventListener('pointercancel', onUp);
      base.remove();
    },
  };
}
